import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Container, Typography, Box, Grid, Card, CardContent, CardActions, 
  Button, Chip, TextField, InputAdornment, Divider,
  Switch, FormControlLabel,
   Tooltip
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Bookmark as BookmarkIcon,
  Download as DownloadIcon,
  Quiz as QuizIcon,
  Assignment as AssignmentIcon,
  Favorite as FavoriteIcon
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { useArticles } from '../context/ArticleContext';

// Mock data for articles
const mockArticles = [
  {
    id: 1,
    title: "Understanding Anxiety: Causes and Coping Mechanisms",
    category: "Mental Health",
    tags: ["anxiety", "stress", "coping"],
    readTime: "8 min read",
    hasQuiz: true,
    hasExercises: true,
    bookmarked: false,
    downloaded: false,
    relevanceScore: 95,
    image: "https://source.unsplash.com/random/400x200/?calm",
    excerpt: "Learn about the root causes of anxiety and discover effective techniques to manage anxious thoughts and feelings in daily life."
  },
  {
    id: 2,
    title: "Building Emotional Resilience in Challenging Times",
    category: "Emotional Resilience",
    tags: ["resilience", "emotions", "growth"],
    readTime: "12 min read",
    hasQuiz: false,
    hasExercises: true,
    bookmarked: true,
    downloaded: true,
    relevanceScore: 87,
    image: "https://source.unsplash.com/random/400x200/?strength",
    excerpt: "Develop stronger emotional resilience with practical strategies that help you bounce back from setbacks and grow through challenges."
  },
  {
    id: 3,
    title: "Mindfulness Practices for Everyday Life",
    category: "Personal Growth",
    tags: ["mindfulness", "meditation", "awareness"],
    readTime: "10 min read",
    hasQuiz: true,
    hasExercises: true,
    bookmarked: false,
    downloaded: false,
    relevanceScore: 92,
    image: "https://source.unsplash.com/random/400x200/?mindfulness",
    excerpt: "Incorporate simple mindfulness techniques into your daily routine to reduce stress and increase your sense of presence and well-being."
  },
  {
    id: 4,
    title: "Improving Sleep Quality for Better Mental Health",
    category: "Mental Health",
    tags: ["sleep", "health", "habits"],
    readTime: "7 min read",
    hasQuiz: false,
    hasExercises: true,
    bookmarked: false,
    downloaded: false,
    relevanceScore: 88,
    image: "https://source.unsplash.com/random/400x200/?sleep",
    excerpt: "Discover how quality sleep directly impacts your mental health and learn practical tips for improving your sleep habits."
  },
  {
    id: 5,
    title: "Navigating Relationship Challenges",
    category: "Relationships",
    tags: ["relationships", "communication", "boundaries"],
    readTime: "15 min read",
    hasQuiz: true,
    hasExercises: false,
    bookmarked: false,
    downloaded: false,
    relevanceScore: 75,
    image: "https://source.unsplash.com/random/400x200/?relationship",
    excerpt: "Learn effective communication strategies and boundary-setting techniques to build healthier, more fulfilling relationships."
  },
  {
    id: 6,
    title: "Finding Purpose and Meaning in Daily Life",
    category: "Personal Growth",
    tags: ["purpose", "meaning", "fulfillment"],
    readTime: "11 min read",
    hasQuiz: true,
    hasExercises: true,
    bookmarked: false,
    downloaded: false,
    relevanceScore: 90,
    image: "https://source.unsplash.com/random/400x200/?purpose",
    excerpt: "Explore practical ways to discover and cultivate a greater sense of purpose and meaning in your everyday activities."
  }
];

// Mock user mood data
// Enhanced mock user mood data with more detailed information
const userMoodTrends = {
  predominantMood: "anxious",
  secondaryMood: "reflective",
  recentImprovement: true,
  suggestedTopics: ["Anxiety", "Resilience", "Mindfulness"],
  recentMoods: [
    { mood: "anxious", count: 5 },
    { mood: "calm", count: 2 },
    { mood: "reflective", count: 3 },
    { mood: "stressed", count: 4 }
  ],
  topTriggers: ["work pressure", "social interactions", "uncertainty"],
  improvementAreas: ["sleep quality", "stress management"]
};

const SelfHelp = () => {
  const navigate = useNavigate();
  const [articles] = useState(mockArticles);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showOnlyBookmarked, setShowOnlyBookmarked] = useState(false);
  const [showOnlyDownloaded, setShowOnlyDownloaded] = useState(false);
  const { isBookmarked, isDownloaded } = useArticles();
  
  // Categories for filtering
  const categories = ['All', 'Mental Health', 'Personal Growth', 'Emotional Resilience', 'Relationships']
  
  // Filter articles based on search, category, and bookmark/download filters
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
    const matchesBookmark = !showOnlyBookmarked || isBookmarked(article.id);
    const matchesDownload = !showOnlyDownloaded || isDownloaded(article.id);
    
    return matchesSearch && matchesCategory && matchesBookmark && matchesDownload;
  });
  

const location = useLocation();
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [location.search]);

  // Sort articles by relevance to user's mood
  const sortedArticles = [...filteredArticles].sort((a, b) => b.relevanceScore - a.relevanceScore);
  
  return (
    <>
    
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 300, 
            color: '#4A306D',
            fontFamily: '"Lato", sans-serif',  // Changed from Poppins to Lato
            mb: 1
          }}
        >
          Self-Help Resources
        </Typography>
        <Typography 
          variant="subtitle1" sx={{ color: '#4A306D', opacity: 0.8, fontFamily: '"Lato", sans-serif' }}>
          Curated guides to support your mental health journey. <br /><br />
        </Typography>      
    

         {/* Personalized Recommendations */}
        <Box 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(108, 141, 250, 0.1) 0%, rgba(255, 159, 126, 0.1) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            right: 0, 
            width: '30%', 
            height: '100%',
            background: 'radial-gradient(circle at top right, rgba(166, 143, 219, 0.2), transparent 70%)',
            zIndex: 0
          }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 500,
                color: '#4A306D',
                fontFamily: '"Quicksand", sans-serif',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FavoriteIcon sx={{ mr: 1, color: '#FF9F7E' }} />
              Personalized For You
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 2, fontFamily: '"Quicksand", sans-serif' }}> 
              Based on your recent mood patterns, we recommend exploring these topics:
            </Typography>
            
            <Button
              variant="contained"
              sx={{ 
                background: 'linear-gradient(135deg, #6C8DFA 0%, #A68FDB 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: '"Quicksand", sans-serif',
                fontWeight: 500
              }}
              onClick={() => {
                // Set search to first suggested topic
                setSearchTerm(userMoodTrends.suggestedTopics[0]);
                setSelectedCategory('All');
              }}
            >
              View Recommended Articles
            </Button>     
          </Box>
        </Box>
            
         {/* Search and Filters */}
        <Box sx={{ mb: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
          <TextField
            placeholder="Search articles, topics, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#6C8DFA' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              height: { xs: 48, md: 60 },
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': {
                  borderColor: '#6C8DFA',
                },
              }
            }}
          />
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(category)}
                sx={{
                  bgcolor: selectedCategory === category ? '#6C8DFA' : 'transparent',
                  color: selectedCategory === category ? 'white' : 'text.primary',
                  border: '1px solid',
                  fontWeight: 500,
                  borderColor: selectedCategory === category ? '#6C8DFA' : 'divider',
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: selectedCategory === category ? '#5A7DE0' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              />
            ))}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {userMoodTrends.suggestedTopics.map((topic, index) => (
                <Chip 
                  key={index} 
                  label={topic} 
                  sx={{ 
                    bgcolor: index === 0 ? 'rgba(255, 159, 126, 0.2)' : 'rgba(108, 141, 250, 0.2)',
                    color: index === 0 ? '#FF9F7E' : '#6C8DFA',
                    fontWeight: 500,
                    borderRadius: 2
                  }} 
                  onClick={() => setSearchTerm(topic)}
                />
              ))}
            </Box>

        {/* Additional Filters */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
             {filteredArticles.length} articles found
            </Typography>
            <FormControlLabel
              control={
                <Switch 
                  checked={showOnlyBookmarked}
                  onChange={(e) => setShowOnlyBookmarked(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#FF9F7E',
                      '& + .MuiSwitch-track': {
                        backgroundColor: 'rgba(255, 159, 126, 0.5)',
                      },
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <BookmarkIcon sx={{ fontSize: 18, mr: 0.5, color: '#FF9F7E' }} />
                  Bookmarked
                </Typography>
              }
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={showOnlyDownloaded}
                  onChange={(e) => setShowOnlyDownloaded(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#6C8DFA',
                      '& + .MuiSwitch-track': {
                        backgroundColor: 'rgba(108, 141, 250, 0.5)',
                      },
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <DownloadIcon sx={{ fontSize: 18, mr: 0.5, color: '#6C8DFA' }} />
                  Available Offline
                </Typography>
              }
            />
          </Box>          

        </Box>
        
        {/* Articles Grid */}
        <Grid container spacing={3}>
          {sortedArticles.map((article) => (
            <Grid item xs={12} md={6} lg={4} key={article.id}>
              <Card 
                elevation={0} 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                    borderColor: article.relevanceScore > 90 ? '#FF9F7E' : '#6C8DFA',
                  }
                }}
              >
                {/* Relevance indicator */}
                {article.relevanceScore > 90 && (
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      bgcolor: 'rgba(255, 159, 126, 0.9)',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      zIndex: 2
                    }}
                  >
                    Recommended for You
                  </Box>
                )}
                
                {/* Article image */}
                <Box 
                  sx={{ 
                    height: 80, 
                    backgroundImage: `url(${article.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '25%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                    }
                  }}
                />
                
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Chip 
                      label={article.category} 
                      size="small"
                      sx={{ 
                        bgcolor: 
                          article.category === 'Mental Health' ? 'rgba(108, 141, 250, 0.1)' :
                          article.category === 'Personal Growth' ? 'rgba(143, 222, 180, 0.1)' :
                          article.category === 'Emotional Resilience' ? 'rgba(255, 159, 126, 0.1)' :
                          'rgba(166, 143, 219, 0.1)',
                        color: 
                          article.category === 'Mental Health' ? '#6C8DFA' :
                          article.category === 'Personal Growth' ? '#8FDEB4' :
                          article.category === 'Emotional Resilience' ? '#FF9F7E' :
                          '#A68FDB',
                        fontWeight: 500,
                        borderRadius: 1
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {article.readTime}
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="h6" 
                    component="h2" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      fontFamily: '"Quicksand", sans-serif',
                      fontSize: '1.1rem',
                      lineHeight: 1.3,
                      mb: 2
                    }}
                  >
                    {article.title}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      mb: 2,
                      fontFamily: '"Lato", sans-serif',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {article.excerpt}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {article.hasQuiz && (
                      <Tooltip title="Includes interactive quiz">
                        <Chip 
                          icon={<QuizIcon fontSize="small" />} 
                          label="Quiz" 
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(108, 141, 250, 0.1)',
                            color: '#6C8DFA',
                            borderRadius: 1
                          }}
                        />
                      </Tooltip>
                    )}
                    
                    {article.hasExercises && (
                      <Tooltip title="Includes practical exercises">
                        <Chip 
                          icon={<AssignmentIcon fontSize="small" />} 
                          label="Exercises" 
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(143, 222, 180, 0.1)',
                            color: '#8FDEB4',
                            borderRadius: 1
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
                
                <Divider />
                
               
                <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
                  <Button 
                    size="small" 
                    onClick={() => navigate(`/self-help/article/${article.id}`)}
                    sx={{ 
                      color: '#4A306D',
                      fontWeight: 500,
                      fontFamily: '"Quicksand", sans-serif',
                    }}
                  >
                    Read Article
                  </Button>              
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* Empty state */}
        {filteredArticles.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No articles found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your search or filters to find what you're looking for
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('All');
                setShowOnlyBookmarked(false);
                setShowOnlyDownloaded(false);
              }}
              sx={{ 
                background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
                borderRadius: 2,
              }}
            >
              Reset Filters
            </Button>
          </Box>
        )}
      </Container>
    </>
  );
};

export default SelfHelp;